import { defineStore } from "pinia";
import axios from "axios";
import { API_URL } from "@/config/api";
import { withShioriAccessContext } from "@/api/shiori-access-context.js";
import {
  createGuestWishlist,
  deleteGuestWishlist,
  fetchGuestWishlist,
  isGuestMode,
  toggleGuestWishlistVote,
  updateGuestWishlist,
} from "@/utils/guest-session";

let recommendationController = null;
let recommendationRequestId = 0;

const isCanceledRequest = (err) =>
  axios.isCancel(err) ||
  err?.code === "ERR_CANCELED" ||
  err?.name === "CanceledError" ||
  err?.name === "AbortError" ||
  err?.message === "canceled";

const normalizeTags = (tags) => {
  if (!Array.isArray(tags)) return [];
  return tags.reduce((normalized, tag) => {
    if (normalized.length >= 10 || typeof tag !== "string") return normalized;
    const trimmedTag = tag.trim();
    if (!trimmedTag || trimmedTag.length > 20 || normalized.includes(trimmedTag)) return normalized;
    normalized.push(trimmedTag);
    return normalized;
  }, []);
};

const normalizeWishlistItem = (item) => ({
  ...item,
  TAGS: normalizeTags(item?.TAGS),
});

const normalizeWishlistItems = (items) =>
  Array.isArray(items) ? items.map(normalizeWishlistItem) : [];

const safeServerDetail = (detail) => {
  if (typeof detail !== "string") return null;
  const normalized = detail.trim();
  const hasControlCharacter = [...normalized].some((character) => {
    const code = character.charCodeAt(0);
    return code < 32 || code === 127;
  });
  if (
    !normalized ||
    normalized.length > 160 ||
    hasControlCharacter ||
    /[<>]/.test(normalized) ||
    /bearer|authorization|token|password|secret|api[-_ ]?key|stack|traceback|sql/i.test(normalized)
  ) {
    return null;
  }
  return normalized;
};

const safeWishlistError = (err, fallback) => {
  const status = err.response?.status;
  if (status === 401) return "ログインの有効期限が切れました。もう一度ログインしてください。";
  if (status === 403) return "この操作には編集権限が必要です。";
  if (status === 404) return "対象の行きたいところが見つかりませんでした。";
  return safeServerDetail(err.response?.data?.detail) || fallback;
};

export const cancelWishlistRecommendations = (store = null) => {
  if (recommendationController) {
    recommendationController.abort();
  }
  recommendationController = null;
  recommendationRequestId += 1;
  if (store) {
    store.recommendationLoading = false;
    store.error = null;
  }
};

export const useWishlistStore = defineStore("wishlist", {
  state: () => ({
    items: {}, // { shioriId: [wishlist items] }
    loading: false,
    recommendationLoading: false,
    recommendations: {},
    error: null,
  }),

  getters: {
    getItemsBySioriId: (state) => (shioriId) => {
      return state.items[shioriId] || [];
    },
  },

  actions: {
    async fetchWishlist(shioriId) {
      this.loading = true;
      this.error = null;
      try {
        if (isGuestMode()) {
          this.items[shioriId] = normalizeWishlistItems(
            fetchGuestWishlist(shioriId),
          );
          return this.items[shioriId];
        }

        const token = localStorage.getItem("token");
        const res = await axios.get(`${API_URL}/wishlist/${shioriId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const normalizedItems = normalizeWishlistItems(res.data);
        this.items[shioriId] = normalizedItems;
        return normalizedItems;
      } catch (err) {
        this.error = safeWishlistError(err, "行きたいところの取得に失敗しました。");
        throw err;
      } finally {
        this.loading = false;
      }
    },

    async createWishlist(data) {
      this.loading = true;
      this.error = null;
      try {
        if (isGuestMode()) {
          const result = createGuestWishlist(data);
          this.items[data.shioriId] = fetchGuestWishlist(data.shioriId);
          return result;
        }

        const token = localStorage.getItem("token");
        const res = await axios.post(`${API_URL}/wishlist/`, data, withShioriAccessContext(data.shioriId, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }));
        await this.fetchWishlist(data.shioriId);
        return res.data;
      } catch (err) {
        this.error = safeWishlistError(err, "行きたいところの作成に失敗しました。");
        throw err;
      } finally {
        this.loading = false;
      }
    },

    async fetchRecommendations(shioriId, theme = null) {
      if (isGuestMode()) {
        this.recommendationLoading = false;
        this.error = "AIおすすめはログイン後に利用できます";
        return {
          canceled: true,
          recommendations: this.recommendations[shioriId] || [],
        };
      }

      if (recommendationController) {
        recommendationController.abort();
      }
      const controller = new AbortController();
      const requestId = recommendationRequestId + 1;
      recommendationRequestId = requestId;
      recommendationController = controller;
      this.recommendationLoading = true;
      this.error = null;
      try {
        const token = localStorage.getItem("token");
        const res = await axios.post(
          `${API_URL}/wishlist/${shioriId}/recommendations`,
          { theme: theme || null },
          withShioriAccessContext(shioriId, {
            timeout: 120000,
            signal: controller.signal,
            headers: { Authorization: `Bearer ${token}` },
          }),
        );
        if (requestId !== recommendationRequestId) {
          return {
            canceled: true,
            recommendations: this.recommendations[shioriId] || [],
          };
        }
        if (Array.isArray(res.data?.recommendations) && res.data.recommendations.length === 6) {
          this.recommendations[shioriId] = res.data.recommendations;
        } else {
          const contractError = new Error(
            "おすすめ候補を正しく取得できませんでした。もう一度お試しください。",
          );
          contractError.isRecommendationContractError = true;
          throw contractError;
        }
        return res.data;
      } catch (err) {
        if (isCanceledRequest(err)) {
          this.error = null;
          return {
            canceled: true,
            recommendations: this.recommendations[shioriId] || [],
          };
        }
        this.error = err.isRecommendationContractError
          ? err.message
          : safeWishlistError(err, "おすすめ候補の取得に失敗しました。");
        throw err;
      } finally {
        if (
          recommendationController === controller &&
          requestId === recommendationRequestId
        ) {
          this.recommendationLoading = false;
          recommendationController = null;
        }
      }
    },

    cancelRecommendations() {
      cancelWishlistRecommendations(this);
    },

    async updateWishlist(shioriId, wishNo, data) {
      this.loading = true;
      this.error = null;
      try {
        if (isGuestMode()) {
          const result = updateGuestWishlist(shioriId, wishNo, data);
          this.items[shioriId] = fetchGuestWishlist(shioriId);
          return result;
        }

        const token = localStorage.getItem("token");
        await axios.put(`${API_URL}/wishlist/${shioriId}/${wishNo}`, data, withShioriAccessContext(shioriId, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }));
        await this.fetchWishlist(shioriId);
      } catch (err) {
        this.error = safeWishlistError(err, "行きたいところの更新に失敗しました。");
        throw err;
      } finally {
        this.loading = false;
      }
    },

    async deleteWishlist(shioriId, wishNo) {
      this.loading = true;
      this.error = null;
      try {
        if (isGuestMode()) {
          const result = deleteGuestWishlist(shioriId, wishNo);
          this.items[shioriId] = fetchGuestWishlist(shioriId);
          return result;
        }

        const token = localStorage.getItem("token");
        await axios.delete(`${API_URL}/wishlist/${shioriId}/${wishNo}`, withShioriAccessContext(shioriId, {
          headers: { Authorization: `Bearer ${token}` },
        }));
        await this.fetchWishlist(shioriId);
      } catch (err) {
        this.error = safeWishlistError(err, "行きたいところの削除に失敗しました。");
        throw err;
      } finally {
        this.loading = false;
      }
    },

    async toggleVote(shioriId, wishNo) {
      try {
        if (isGuestMode()) {
          const result = toggleGuestWishlistVote(shioriId, wishNo);
          this.items[shioriId] = fetchGuestWishlist(shioriId);
          return result;
        }

        const token = localStorage.getItem("token");
        const res = await axios.post(
          `${API_URL}/wishlist/${shioriId}/${wishNo}/vote`,
          {},
          withShioriAccessContext(shioriId, {
            headers: { Authorization: `Bearer ${token}` },
          }),
        );
        // ローカルステート楽観的更新
        const items = this.items[shioriId] || [];
        const item = items.find((i) => i.WISH_NO === wishNo);
        if (item) {
          item.MY_VOTE = res.data.voted;
          item.VOTE_COUNT = res.data.voted
            ? item.VOTE_COUNT + 1
            : item.VOTE_COUNT - 1;
        }
        return res.data;
      } catch (err) {
        this.error = safeWishlistError(err, "投票に失敗しました。");
        throw err;
      }
    },
    clearShioriWishlist(shioriId) {
      delete this.items[shioriId];
      delete this.recommendations[shioriId];
      this.error = null;
    },
  },
});
